/* global window location */

import queryString from 'query-string';
import {
    createAnalytics,
    createIdentityJwtTokenGetter
} from '@3plearning/mx-teacher-analytics';
import appEvents, { ANALYTICS_EVENTS, ANALYTICS_MODULES } from './events';

const APP_NAME = 'school-roll';
const TEACHER_TOTTER_PRODUCT = 'mathletics';
const CORE_GROUP = 'core';

let client = null;

function getConfigValue(config, ...keys) {
    for (const key of keys) {
        if (config && config[key] !== undefined) {
            return config[key];
        }
    }

    return undefined;
}

function isTruthy(value) {
    return value === true || value === 'true';
}

function getQueryParam(key) {
    const params = queryString.parse(location.search);
    return params[key] || '';
}

function getAppConfig() {
    // eslint-disable-next-line no-underscore-dangle
    return window._env_ || window.pppAppConfig || undefined;
}

function resolveEnabledGroups(trackingGroups) {
    if (!trackingGroups) {
        return null;
    }

    const groups = trackingGroups
        .split(',')
        .map(group => group.trim())
        .filter(Boolean);

    return [...groups, CORE_GROUP];
}

function mapUserProfileType(type) {
    switch (String(type)) {
    case '2':
        return 'teacher';
    case '3':
        return 'admin';
    default:
        return 'unknown';
    }
}

function getIdentity(locale) {
    return {
        locale,
        countryCode: getQueryParam('countryCode'),
        userProfileId: getQueryParam('userProfileId'),
        userProfileType: mapUserProfileType(getQueryParam('userProfileType')),
        marketRegionId: getQueryParam('marketRegionId') || getQueryParam('regionId')
    };
}

function buildAnalyticsClient(config) {
    const environment = getConfigValue(config, 'ENVIRONMENT', 'env') || getQueryParam('env') || 'live';
    const teacherTotterSyncUrl = getConfigValue(
        config,
        'TEACHER_TOTTER_SYNC_URL',
        'AnalyticsTeacherTotterSyncUrl'
    ) || '';
    const identityGatewayUrl = getConfigValue(
        config,
        'IDENTITY_GATEWAY_URL',
        'AnalyticsIdentityGatewayUrl'
    ) || '';
    const teacherTotterEnabled = isTruthy(getConfigValue(
        config,
        'TEACHER_TOTTER_ENABLED',
        'TeacherTotterEnabled'
    )) && getQueryParam('isOneRosterActivatedSchool') === 'true';
    const posthogEnabled = isTruthy(getConfigValue(config, 'POSTHOG_ENABLED', 'PostHogEnabled'));

    const analyticsConfig = {
        app: APP_NAME,
        environment,
        providers: {
            posthog: posthogEnabled ? { enabled: true } : { enabled: false },
            teacherTotter: {
                enabled: teacherTotterEnabled && Boolean(teacherTotterSyncUrl),
                syncUrl: teacherTotterSyncUrl,
                product: TEACHER_TOTTER_PRODUCT,
                getAuthToken: createIdentityJwtTokenGetter({
                    identityGatewayUrl,
                    getAppAuthToken: () => getQueryParam('authToken') || null
                })
            }
        },
        enabledFeatureGroups: resolveEnabledGroups(getConfigValue(
            config,
            'POSTHOG_TRACKING_GROUPS',
            'PostHogTrackingGroups'
        )),
        events: appEvents
    };

    try {
        return createAnalytics(analyticsConfig);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[analytics] Initialisation failed; continuing with teacher-totter disabled.', error);

        return createAnalytics({
            ...analyticsConfig,
            providers: {
                ...analyticsConfig.providers,
                teacherTotter: {
                    ...analyticsConfig.providers.teacherTotter,
                    enabled: false
                }
            }
        });
    }
}

export function initialiseAnalytics(locale) {
    if (client) {
        return client;
    }

    client = buildAnalyticsClient(getAppConfig() || {});
    try {
        client.setCommonProps(getIdentity(locale));
        client.identify();
    } catch (error) {
        // no-op
    }

    return client;
}

export function getAnalytics() {
    return client || initialiseAnalytics();
}

export function trackEvent(name, props = {}) {
    try {
        const result = getAnalytics().track(name, props);
        if (result && typeof result.catch === 'function') {
            result.catch(() => {});
        }
    } catch (error) {
        // no-op
    }
}

export function trackSession(task) {
    try {
        getAnalytics().trackSession(task);
    } catch (error) {
        // no-op
    }
}

function getModuleFromPath(path) {
    if (path.indexOf('/quickUpload') === 0) {
        return ANALYTICS_MODULES.quickUpload;
    }

    if (path.indexOf('/students') === 0 || path.indexOf('/edit-student') === 0) {
        return ANALYTICS_MODULES.students;
    }

    if (path.indexOf('/teachers') === 0 || path.indexOf('/edit-teacher') === 0) {
        return ANALYTICS_MODULES.teachers;
    }

    if (path.indexOf('/manage-default-courses') === 0) {
        return ANALYTICS_MODULES.manageDefaultCourses;
    }

    if (path.indexOf('/manage-assigned-courses') === 0) {
        return ANALYTICS_MODULES.manageAssignedCourses;
    }

    if (path.indexOf('/teacher-view') === 0) {
        return ANALYTICS_MODULES.teacherView;
    }

    if (path.indexOf('/rollover') === 0) {
        return ANALYTICS_MODULES.rollover;
    }

    if (path.indexOf('/download-certificates') === 0) {
        return ANALYTICS_MODULES.downloadCertificates;
    }

    if (path.indexOf('/sign-in-cards') === 0) {
        return ANALYTICS_MODULES.signInCards;
    }

    if (path.indexOf('/extend-result') === 0) {
        return ANALYTICS_MODULES.extendResult;
    }

    if (path.indexOf('/assign-user') === 0) {
        if (path.indexOf('/classToTeacher') !== -1 || path.indexOf('/teacherToClass') !== -1) {
            return ANALYTICS_MODULES.teachers;
        }

        if (path.indexOf('/classToStudent') !== -1) {
            return ANALYTICS_MODULES.students;
        }
    }

    return 'school_roll';
}

export function trackRoute(path) {
    const module = getModuleFromPath(path);

    trackEvent(ANALYTICS_EVENTS.pageView, { path, module });
    trackEvent(ANALYTICS_EVENTS.routeViewed, { path, module });
    trackSession(path);
}



// WEBPACK FOOTER //
// ./app/analytics/index.js